<template>
  <tr
    :key="item.id"
    :data-cy="`device${item.deviceSerial}Row`"
    :class="{ 'child-expanded-row': isChild }"
    v-resize="handleResize"
  >
    <td class="col-expand" data-cy="columnExpand">
      <TableExpandButton
        v-if="itemCanBeExpanded"
        :isExpanded="isExpanded"
        @click="expand()"
      />
    </td>
    <td v-if="item.deviceName" style="max-width: 300px" data-cy="columnName">
      <v-tooltip location="bottom" :max-width="600" :disabled="!isNameOverflowing">
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="device-name" :ref="item.id + 'Name'">
            <span>{{ item.deviceName }}</span>
          </div>
        </template>
        <span>{{ item.deviceName }}</span>
      </v-tooltip>
    </td>
    <td v-else>{{ $t('manageDevices.unnamedDevice') }}</td>
    <td style="max-width: 300px" data-cy="columnLocationName">
      <v-tooltip
        v-if="item.deviceLocationName"
        location="bottom"
        :max-width="600"
        :disabled="!isLocationNameOverflowing"
      >
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="device-name" :ref="item.id + 'LocationName'">
            <span>{{ item.deviceLocationName }}</span>
          </div>
        </template>
        <span>{{ item.deviceLocationName }}</span>
      </v-tooltip>
      <span v-else class="not-available-text">
        {{ $t('manageDevices.notAvailable') }}
      </span>
    </td>
    <td data-cy="columnDevice">
      {{ item.deviceType }}
    </td>
    <td style="max-width: 120px" data-cy="columnModel">
      {{ item.deviceModel }}
    </td>
    <td data-cy="columnSerial">
      {{ item.deviceSerial }}
    </td>
    <td data-cy="columnSubscription">
      <span
        :class="{
          'not-available-text': !item.showSubscription || !canAccess(access.FULL, item.accessCode)
        }"
        >{{ item.deviceSubscription }}</span
      >
    </td>
    <td data-cy="columnAccess">
      {{ item.access }}
    </td>
    <td data-cy="columnCondition" style="min-width: 120px">
      <TableCondition
        v-if="item.showCondition"
        :deviceId="item.id"
        :accessLevel="item.accessCode"
      />
    </td>
    <td
      style="min-width: 184px"
      :class="{ 'not-available-text': !item.showLastCommunication }"
      data-cy="columnLastCommunication"
    >
      <span v-if="item.lastCommunication">{{ item.lastCommunication }}</span>
      <span v-else>{{ $t('manageDevices.lastCommunicationNever') }}</span>
    </td>
    <td data-cy="columnAqi">
      <StyledReading
        data-cy="aqiIcon"
        v-if="item.showAQI"
        :reading="item.reading"
        :aqiType="aqiType"
        :measurementType="measurementType"
      />
    </td>
    <td class="right-aligned" data-cy="columnActions">
      <ActionMenu :deviceId="item.id" :accessLevel="item.accessCode" @openDialog="openDialog" />
    </td>
  </tr>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import debounce from 'lodash/debounce'
import { deviceModels } from '@/store/subscriptions/utils'
import { useAccess } from '@/services/device-access'
import { isChildOverflowing } from '@/services/html-utilities'
import ActionMenu from './actionMenu.vue'
import StyledReading from './styledReading.vue'
import TableCondition from './tableCondition.vue'
import TableExpandButton from '@/components/common/buttons/TableExpandButton.vue'
import type { ManageDeviceRow } from '@/views/manage-devices.vue'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'

export default defineComponent({
  name: 'DeviceTableBody',
  emits: ['openDialog'],
  props: {
    item: {
      type: Object as PropType<ManageDeviceRow>,
      required: true
    },
    expand: {
      type: Function as PropType<() => void>,
      default: (): void => undefined
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    isChild: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ActionMenu,
    StyledReading,
    TableCondition,
    TableExpandButton
  },
  setup() {
    const { canAccess, access } = useAccess()
    return { canAccess, access }
  },
  data() {
    return {
      isNameOverflowing: false as boolean,
      isLocationNameOverflowing: false as boolean,
      resizeHandler: null as null | (() => void)
    }
  },
  methods: {
    handleResize(): void {
      this.resizeHandler?.()
    },
    openDialog(dialogData: { dialog: string; deviceId: string }): void {
      this.$emit('openDialog', dialogData)
    }
  },
  computed: {
    aqiType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
    },
    measurementType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    },
    itemCanBeExpanded(): boolean {
      const res =
        this.item.deviceModel === deviceModels.SMART_BRIDGE.toUpperCase() &&
        this.item.associatedDevices?.length > 0
      return res
    }
  },
  created(): void {
    this.resizeHandler = debounce(() => {
      this.isNameOverflowing = isChildOverflowing(this.$refs[this.item.id + 'Name'] as HTMLElement)
      this.isLocationNameOverflowing = isChildOverflowing(
        this.$refs[this.item.id + 'LocationName'] as HTMLElement
      )
    }, 200)
  }
})
</script>

<style lang="scss" scoped>
.right-aligned {
  direction: rtl;
}
.device-name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}

tr.child-expanded-row {
  background-color: var(--tsi-color--dark-8);
}

td.col-expand {
  padding: 0 0 0 8px !important;
}
</style>
